import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FileText, FolderOpen, Plus } from 'lucide-react';
import LessonCard from '@/components/lessons/LessonCard';
import LessonCreateDialog from '@/components/lessons/LessonCreateDialog';
import SubmissionsDialog from '@/components/shared/SubmissionsDialog';
import EditScheduleDialog from '@/components/shared/EditScheduleDialog';
import ResourceReuseModal from '@/components/shared/ResourceReuseModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonsTab({ classId }) {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const isStudent = user?.role === 'student';

  const [lessons, setLessons] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [editScheduleItem, setEditScheduleItem] = useState(null);
  const [reuseItem, setReuseItem] = useState(null);

  const fetchClassStudents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classes/${classId}`, headers);
      setClassStudents((res.data.members || []).filter(m => m.role === 'student'));
    } catch (err) { console.error("[LessonsTab] silent error:", err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, token]);

  const fetchLessons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/content/list/${classId}/lesson`, headers);
      setLessons(res.data);
    } catch (err) { console.error("[LessonsTab] silent error:", err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, token]);

  useEffect(() => { fetchLessons(); fetchClassStudents(); }, [fetchLessons, fetchClassStudents]);

  const handleLessonUpdated = (id, patch) => {
    if (patch) {
      setLessons(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
    } else {
      fetchLessons(); // student submission changed
    }
  };

  const handleDelete = async (contentId) => {
    try {
      await axios.delete(`${API}/content/delete/${contentId}`, headers);
      toast.success(t('success'));
      fetchLessons();
    } catch { toast.error(t('error')); }
  };

  const handleReview = async (contentId) => {
    try {
      const res = await axios.get(`${API}/content/submissions/${contentId}`, headers);
      setSubmissions(res.data);
      setReviewingId(contentId);
    } catch { toast.error(t('error')); }
  };

  const handleUpdateSchedule = async (scheduled_for) => {
    if (!editScheduleItem) return;
    try {
      await axios.put(`${API}/content/update/${editScheduleItem.id}`, { scheduled_for }, headers);
      toast.success(t('schedule_updated'));
      setEditScheduleItem(null);
      fetchLessons();
    } catch { toast.error(t('error')); }
  };

  const folders = [...new Set(lessons.map(l => l.folder).filter(Boolean))];
  const filteredLessons = selectedFolder ? lessons.filter(l => l.folder === selectedFolder) : lessons;

  return (
    <div data-testid="lessons-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setSelectedFolder('')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${!selectedFolder ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {t('all_units')}
          </button>
          {folders.map(f => (
            <button key={f} onClick={() => setSelectedFolder(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedFolder === f ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              <FolderOpen className="w-3 h-3 inline mr-1" />{f}
            </button>
          ))}
        </div>
        {!isStudent && (
          <Button data-testid="create-lesson-btn" onClick={() => setShowCreate(true)} size="sm" className="gap-1 bg-blue-600 hover:bg-blue-700 rounded-lg">
            <Plus className="w-4 h-4" /> {t('create')}
          </Button>
        )}
      </div>

      {filteredLessons.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>{t('no_items')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLessons.map(lesson => (
            <LessonCard
              key={lesson.id}
              lesson={lesson}
              isStudent={isStudent}
              token={token}
              t={t}
              onUpdated={handleLessonUpdated}
              onOpenSchedule={setEditScheduleItem}
              onReview={handleReview}
              onDelete={handleDelete}
              onReuse={setReuseItem}
              classStudents={classStudents}
            />
          ))}
        </div>
      )}

      <LessonCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        classId={classId}
        token={token}
        t={t}
        classStudents={classStudents}
        isStudent={isStudent}
        onCreated={fetchLessons}
      />

      <SubmissionsDialog
        open={!!reviewingId}
        onClose={() => setReviewingId(null)}
        title={t('review_submissions')}
        submissions={submissions}
        token={token}
        t={t}
        contentId={reviewingId}
      />

      <EditScheduleDialog
        item={editScheduleItem}
        onClose={() => setEditScheduleItem(null)}
        onSave={handleUpdateSchedule}
        t={t}
      />

      <ResourceReuseModal
        open={!!reuseItem}
        onClose={() => setReuseItem(null)}
        resourceTitle={reuseItem?.title}
        contentId={reuseItem?.id}
        token={token}
        scheduledAssignments={reuseItem?.scheduled_assignments || []}
        classStudents={classStudents}
        currentAssignedTo={reuseItem?.assigned_to}
        onAssign={async (ids, scheduledFor) => {
          const res = await axios.post(`${API}/content/${reuseItem.id}/assign-students`, { student_ids: ids, scheduled_for: scheduledFor }, headers);
          return res.data;
        }}
        onAssigned={fetchLessons}
      />
    </div>
  );
}
